import { useState } from 'react'
import { Send, Mail, CheckCircle, LogIn } from 'lucide-react'
import { collection, addDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@/services/firebase/config'
import { useAuthStore } from '@/store/useAuthStore'
import { useUIStore } from '@/store/uiStore'
import { Button } from '@/components/common/Button'
import { checkRateLimit, recordAttempt } from '@/utils/rateLimit'
import { formatPrice } from '@/utils/format'

interface EnquiryFormProps {
  listingId: string
  listingTitle: string
  listingImage?: string
  listingPrice?: number | null
  sellerId: string
  sellerName: string
  sellerEmail: string
}

export function EnquiryForm({
  listingId,
  listingTitle,
  listingImage,
  listingPrice,
  sellerId,
  sellerName,
  sellerEmail,
}: EnquiryFormProps) {
  const { isAuthenticated, user, profile } = useAuthStore()
  const { openModal, showToast } = useUIStore()
  const [message, setMessage] = useState('')
  const [phone, setPhone] = useState(profile?.phone || '')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  if (!isAuthenticated) {
    return (
      <div className="card p-6 text-center">
        <Mail className="w-8 h-8 text-slate-400 mx-auto mb-3" />
        <p className="text-slate-600 mb-3">Sign in to send an enquiry to this seller.</p>
        <Button variant="primary" onClick={() => openModal('login')}>
          <LogIn className="w-4 h-4 mr-2" />
          Sign In
        </Button>
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="card p-6 text-center">
        <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
        <h3 className="font-semibold text-lg mb-1">Enquiry Sent</h3>
        <p className="text-sm text-slate-500">
          Your enquiry has been emailed to {sellerName}. They'll get back to you at{' '}
          <strong>{user?.email}</strong>.
        </p>
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (message.trim().length < 10) {
      showToast('Please write at least 10 characters', 'error')
      return
    }

    if (!checkRateLimit('enquiry')) {
      showToast('Too many enquiries. Please wait before sending another.', 'error')
      return
    }

    setIsSubmitting(true)

    try {
      const enquiryData = {
        // Sender info
        senderId: user!.uid,
        senderName: user!.displayName || profile?.displayName || 'Anonymous',
        senderEmail: user!.email,
        senderPhone: phone.trim() || null,
        // Listing info
        listingId,
        listingTitle,
        listingImage: listingImage || null,
        listingPrice: listingPrice ?? null,
        // Recipient info
        sellerId,
        sellerName,
        sellerEmail,
        // Message
        message: message.trim(),
        // Meta
        status: 'sent',
        createdAt: serverTimestamp(),
      }

      // Save to enquiries collection (for seller's dashboard later)
      await addDoc(collection(db, 'enquiries'), enquiryData)

      // Send enquiry email via Cloud Function
      const { getFunctions, httpsCallable } = await import('firebase/functions')
      const functions = getFunctions(undefined, 'australia-southeast1')
      const sendEnquiry = httpsCallable(functions, 'sendEnquiryEmailFn')
      await sendEnquiry({
        sellerEmail,
        sellerName,
        buyerName: enquiryData.senderName,
        buyerEmail: user!.email,
        buyerPhone: phone.trim() || null,
        listingTitle,
        listingPrice: listingPrice ? formatPrice(listingPrice) : null,
        listingUrl: `${window.location.origin}/listing/${listingId}`,
        message: message.trim(),
      })

      recordAttempt('enquiry')
      setSubmitted(true)
    } catch (error) {
      console.error('Failed to send enquiry:', error)
      showToast('Failed to send enquiry. Please try again.', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="card p-6">
      <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
        <Mail className="w-5 h-5 text-primary-500" />
        Send an Enquiry
      </h3>

      {/* Listing context */}
      <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-3 mb-4">
        {listingImage && (
          <img
            src={listingImage}
            alt={listingTitle}
            className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
          />
        )}
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-800 line-clamp-2 break-words">{listingTitle}</p>
          {listingPrice != null && (
            <p className="text-sm text-primary-600 font-semibold">{formatPrice(listingPrice)}</p>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Pre-filled sender info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="enquiry-name" className="block text-sm font-medium text-slate-700 mb-1">Your Name</label>
            <input
              id="enquiry-name"
              type="text"
              value={user?.displayName || profile?.displayName || ''}
              disabled
              className="input bg-slate-50 text-slate-500"
            />
          </div>
          <div>
            <label htmlFor="enquiry-email" className="block text-sm font-medium text-slate-700 mb-1">Your Email</label>
            <input
              id="enquiry-email"
              type="email"
              value={user?.email || ''}
              disabled
              className="input bg-slate-50 text-slate-500"
            />
          </div>
        </div>

        <div>
          <label htmlFor="enquiry-phone" className="block text-sm font-medium text-slate-700 mb-1">
            Phone Number <span className="text-slate-400">(optional)</span>
          </label>
          <input
            id="enquiry-phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="e.g. 021 123 4567"
            className="input"
          />
        </div>

        <div>
          <label htmlFor="enquiry-message" className="block text-sm font-medium text-slate-700 mb-1">Message</label>
          <textarea
            id="enquiry-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={`Hi ${sellerName}, I'm interested in "${listingTitle}"...`}
            className="input min-h-[100px]"
            maxLength={2000}
            required
          />
          <p className="text-xs text-slate-400 mt-1 text-right">{message.length}/2000</p>
        </div>

        <Button
          type="submit"
          variant="primary"
          className="w-full"
          disabled={isSubmitting || message.trim().length < 10}
        >
          {isSubmitting ? (
            <>Sending...</>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Send Enquiry to {sellerName}
            </>
          )}
        </Button>

        <p className="text-xs text-slate-400 text-center">
          Your contact details will be shared with the seller so they can reply to you.
        </p>
      </form>
    </div>
  )
}
